import { SearchAccordion as ComponentSearchAccordion } from '@/components';
import {
  useCreateQueryStore,
  useQueryStore,
} from '@/features/search/_hooks/query-store';
import { generateQueryAndLeafs } from '@/features/search/_utils/query-store';
import { WorkflowTemplateSearchFormSchema } from '@/features/workflow/models/template';
import { useWorkFlowTemplatePageSearchQueryStates } from '@/features/workflow/utils/search-query-states';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import isEqual from 'lodash-es/isEqual';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import type { z } from 'zod';
import { WorkflowTemplateSearchForm } from './WorkflowTemplateSearchForm';

type FormValues = z.infer<typeof WorkflowTemplateSearchFormSchema>;

export const WorkflowTemplateSearchAccordion: FC = () => {
  const { searchQueryStates, setSearchQueryStates } =
    useWorkFlowTemplatePageSearchQueryStates();

  const { mutateAsync } = useCreateQueryStore();
  const treeParamsData = useQueryStore(searchQueryStates.cascader ?? undefined);

  const defaultValues = useMemo<FormValues>(() => {
    return {
      q: searchQueryStates.q ?? '',
      nestedOrganizationIds: treeParamsData.data?.query?.organizationIds ?? [],
    };
  }, [searchQueryStates.q, treeParamsData.data?.query?.organizationIds]);

  const formMethods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(WorkflowTemplateSearchFormSchema),
  });

  const { setValue } = formMethods;

  useEffect(() => {
    // NOTE: カスケーダーの選択肢がある場合は、その選択肢を設定する
    if (defaultValues.nestedOrganizationIds?.length) {
      setValue('nestedOrganizationIds', defaultValues.nestedOrganizationIds);
    }
  }, [setValue, defaultValues.nestedOrganizationIds]);

  const onSearch = useCallback(
    async ({ q, nestedOrganizationIds }: FormValues) => {
      const queryAndLeafs = generateQueryAndLeafs({
        organizationIds: nestedOrganizationIds,
      });

      const currLeafs = queryAndLeafs?.queryLeafs;
      const prevLeafs = treeParamsData.data?.queryLeafs;

      const isDifferent = currLeafs && !isEqual(currLeafs, prevLeafs);
      const requestCascaderId = nestedOrganizationIds.length
        ? isDifferent
          ? (await mutateAsync(queryAndLeafs)).id
          : searchQueryStates.cascader
        : null;

      const nextSearchQueryStates = {
        cascader: requestCascaderId,
        q: q || null,
        page: 1,
      };
      setSearchQueryStates(nextSearchQueryStates);
    },
    [
      mutateAsync,
      setSearchQueryStates,
      searchQueryStates.cascader,
      treeParamsData.data?.queryLeafs,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      <ComponentSearchAccordion
        defaultOpen={true}
        filterTags={null}
        searchForm={
          <WorkflowTemplateSearchForm
            onSearch={onSearch}
            resetFormValues={{
              q: '',
              nestedOrganizationIds: [],
            }}
          />
        }
      />
    </FormProvider>
  );
};
